import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #actualCartCount = 0;

  /** @type {number} */
  get currentCartCount() {
    // Return the stored actual count, not from textContent
    return this.#actualCartCount;
  }

  set currentCartCount(value) {
    const count = Math.max(0, value || 0);
    this.#actualCartCount = count;
    
    // Always display the count, no limit
    if (count > 0) {
      this.refs.cartBubbleCount.textContent = String(count);
    } else {
      // For count === 0
      this.refs.cartBubbleCount.textContent = '';
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize actual count from DOM if available
    const initialText = this.refs.cartBubbleCount.textContent?.trim();
    if (initialText) {
      const parsed = parseInt(initialText, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        this.#actualCartCount = parsed;
      }
    }

    // Listen to theme's cart update event
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    
    // Listen to common bundle app events
    this.#setupBundleAppListeners();
    
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.#removeBundleAppListeners();
  }

  /** @type {string[] | undefined} */
  #bundleEventNames;

  /**
   * Sets up listeners for common bundle app events
   */
  #setupBundleAppListeners = () => {
    // Common events from bundle apps
    const bundleEvents = [
      'cart:updated',
      'cart:add',
      'ajaxCart:updated',
      'ajaxCart:add',
      'bundle:added',
      'bundle:cart:updated',
      'cart:change',
      'cart:refresh'
    ];

    bundleEvents.forEach((eventName) => {
      document.addEventListener(eventName, this.#onBundleAppEvent);
    });

    // Store event names for cleanup
    this.#bundleEventNames = bundleEvents;
  };

  /**
   * Removes bundle app event listeners
   */
  #removeBundleAppListeners = () => {
    if (this.#bundleEventNames) {
      this.#bundleEventNames.forEach((eventName) => {
        document.removeEventListener(eventName, this.#onBundleAppEvent);
      });
    }
  };

  /**
   * Handles events from bundle apps
   * @param {Event} event
   */
  #onBundleAppEvent = async (event) => {
    // Always fetch cart count from API to ensure accuracy
    // Bundle apps may not provide accurate count in event detail
    const itemCount = await this.#fetchCartCount();

    if (itemCount >= 0) {
      this.renderCartBubble(itemCount, false);
    }
  };

  /**
   * Fetches cart count from Shopify Cart API
   * @returns {Promise<number>}
   */
  #fetchCartCount = async () => {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return this.currentCartCount;
      }

      const cart = await response.json();
      return cart.item_count || 0;
    } catch (error) {
      console.warn('Failed to fetch cart count:', error);
      return this.currentCartCount;
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    
    // For product form additions, fetch actual cart count from API to ensure accuracy
    // The itemCount in event is the quantity added, not the total cart count
    if (comingFromProductForm) {
      const actualCartCount = await this.#fetchCartCount();
      this.renderCartBubble(actualCartCount, false);
    } else {
      // For other sources (like cart updates), use the itemCount from event
      const itemCount = event.detail.data?.itemCount ?? 0;
      this.renderCartBubble(itemCount, false);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form (deprecated, kept for compatibility).
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Ensure itemCount is a valid number
    const count = Math.max(0, itemCount || 0);

    // Show/hide cart bubble based on count
    if (count > 0) {
      this.refs.cartBubbleCount.classList.remove('hidden');
      this.refs.cartBubble.classList.remove('visually-hidden');
      this.classList.add('header-actions__cart-icon--has-cart');
    } else {
      this.refs.cartBubbleCount.classList.add('hidden');
      this.refs.cartBubble.classList.add('visually-hidden');
      this.classList.remove('header-actions__cart-icon--has-cart');
    }

    // Set the cart count
    this.currentCartCount = count;

    // Add animation class if needed
    if (count > 0 && animate) {
      this.refs.cartBubble.classList.add('cart-bubble--animating');
    } else {
      this.refs.cartBubble.classList.remove('cart-bubble--animating');
    }

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        // Only update if the stored count is different from current actual count
        if (!isNaN(count) && count >= 0 && count !== this.#actualCartCount) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
